import React, {useCallback, useMemo} from 'react';

import {Analytics} from '../../../../services/analytics';
import {SettingLabel} from '../components/SettingLabel';
import {SettingSelect} from '../components/SettingSelect';
import {
  type SemanticLegendSettingsWithCategoricalConfig,
  YAxisValues,
} from '../types';

type YValueSelectOption = {
  value: string;
  label: string;
};

export type SemanticLegendYValueProps = {
  yValue: YAxisValues;
  updateSetting: (
    settings: Partial<SemanticLegendSettingsWithCategoricalConfig>
  ) => void;
};

export const SemanticLegendYValueWithCategoricalConfig = ({
  updateSetting,
  yValue,
}: SemanticLegendYValueProps) => {
  const yValueOptions = useMemo(() => getYValueOptions(yValue), [yValue]);

  const yValueOption = useMemo(() => {
    return yValueOptions.find(option => option.value === yValue);
  }, [yValue, yValueOptions]);

  const onYValueChange = useCallback(
    (selected: YValueSelectOption | null) => {
      if (selected != null) {
        Analytics.track('Semantic legend y value changed', {
          newValue: selected?.value,
        });
        updateSetting({
          yAxisValue: selected.value as YAxisValues,
          shouldRecalculateBuckets: true, // we want to recalculate the buckets bounds when the y value changes
        });
      }
    },
    [updateSetting]
  );

  const title =
    yValue === YAxisValues.Config_Categories ||
    yValue === YAxisValues.Config_Number
      ? 'Grouping'
      : 'Y value';

  return (
    <div className={'flex flex-col'} data-test="y-value-setting">
      <div className="flex w-full">
        <SettingLabel text={title} className="ml-14 w-[100px]" />
        <div className="flex w-full flex-row items-center gap-8" key={yValue}>
          <SettingSelect
            ddActionName="Semantic legend value"
            className="grow"
            options={yValueOptions}
            value={yValueOption}
            onChange={onYValueChange}
          />
        </div>
      </div>
    </div>
  );
};

const yValueOptionsMeta: Record<string, {displayName: string}> = {
  ['min']: {
    displayName: 'Min',
  },
  ['max']: {
    displayName: 'Max',
  },
  ['last']: {
    displayName: 'Latest',
  },
};

const yValueOptionsConfig: Record<string, {displayName: string}> = {
  ['config_number']: {
    displayName: 'Continuous',
  },
  ['config_categories']: {
    displayName: 'Discrete',
  },
};

const getYValueOptions = (yValue: YAxisValues): YValueSelectOption[] => {
  const yValueOptions =
    yValue === YAxisValues.Config_Number ||
    yValue === YAxisValues.Config_Categories
      ? yValueOptionsConfig
      : yValueOptionsMeta;

  return Object.keys(yValueOptions).map(val => ({
    label: yValueOptions[val].displayName,
    value: val,
  }));
};
